import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert,
  Button,
  Combobox,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
  useCombobox
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

import { useGetNpiTypeaheadQuery } from '@/store/slices/api/npiApiSlice';
import {
  useClearXifinOrderingPhysicianMutation,
  useGetXifinOrderingPhysicianQuery,
  useUpdateXifinOrderingPhysicianMutation
} from '@/store/slices/api/patientsApiSlice';
import { isValidNpiChecksum } from '@/utils/npi';

import ConfirmationModal from '@/ui/ConfirmationModal';
import LabeledField from '@/ui/LabeledField';

import type { NpiTypeaheadResponse } from '@/types/npi';

interface Props {
  patientId: string;
}

type ApiError = { data?: { message?: string | string[] } };

function errorMessage(error: unknown, fallback: string): string {
  const message = (error as ApiError)?.data?.message;
  return Array.isArray(message) ? message.join(', ') : message || fallback;
}

function physicianName(provider: {
  firstName: string | null;
  lastName: string | null;
  credential?: string | null;
}): string {
  const name = [provider.firstName, provider.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  const credential = provider.credential?.trim();
  return [name, credential].filter(Boolean).join(', ');
}

function physicianLabel(provider: {
  npi: string;
  firstName: string | null;
  lastName: string | null;
  credential?: string | null;
}): string {
  return [physicianName(provider), provider.npi].filter(Boolean).join(' — ');
}

export default function XifinOrderingPhysician({ patientId }: Props) {
  const [opened, { open, close }] = useDisclosure(false);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search.trim(), 300);
  const [selected, setSelected] = useState<NpiTypeaheadResponse['items'][number] | null>(null);
  const [manual, setManual] = useState(false);
  const [npi, setNpi] = useState('');
  const [npiTouched, setNpiTouched] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [credential, setCredential] = useState('');
  const combobox = useCombobox();
  const comboboxRef = useRef(combobox);
  comboboxRef.current = combobox;

  const { data, isLoading, isError } = useGetXifinOrderingPhysicianQuery(patientId);
  const [update, updateState] = useUpdateXifinOrderingPhysicianMutation();
  const [clear, clearState] = useClearXifinOrderingPhysicianMutation();
  const { currentData: matches, isFetching } = useGetNpiTypeaheadQuery(debouncedSearch, {
    skip: debouncedSearch.length < 3 || !opened || Boolean(selected)
  });

  const options = useMemo(() => matches?.items ?? [], [matches?.items]);

  useEffect(() => {
    if (!isFetching && options.length > 0) {
      comboboxRef.current.selectFirstOption();
    }
  }, [isFetching, options]);

  const reset = () => {
    setSearch('');
    setSelected(null);
    setManual(false);
    setNpi('');
    setNpiTouched(false);
    setFirstName('');
    setLastName('');
    setCredential('');
    setConfirmingClear(false);
  };
  const closeModal = () => {
    reset();
    close();
  };

  const save = async () => {
    const body = selected
      ? { npi: selected.npi }
      : {
          npi: npi.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          credential: credential.trim() || undefined
        };
    try {
      await update({ patientId, body }).unwrap();
      notifications.show({
        title: 'Saved',
        message: 'XIFIN ordering physician updated.',
        color: 'teal'
      });
      closeModal();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: errorMessage(error, 'XIFIN ordering physician could not be updated.'),
        color: 'red'
      });
    }
  };

  const handleClear = async () => {
    try {
      await clear(patientId).unwrap();
      notifications.show({
        title: 'Cleared',
        message: 'XIFIN ordering physician cleared.',
        color: 'teal'
      });
      closeModal();
    } catch (error) {
      setConfirmingClear(false);
      notifications.show({
        title: 'Error',
        message: errorMessage(error, 'XIFIN ordering physician could not be cleared.'),
        color: 'red'
      });
    }
  };

  const current = data?.practitioner;
  const currentNpi = data?.npi?.trim() || '';
  const validNpi = isValidNpiChecksum(npi.trim());
  const manualValid = Boolean(validNpi && firstName.trim() && lastName.trim());
  const npiError =
    npiTouched && npi.length > 0 && npi.length < 10
      ? 'Enter all 10 digits of the NPI.'
      : npi.length === 10 && !validNpi
        ? 'Enter a valid NPI.'
        : undefined;
  const searchIsCurrent = search.trim() === debouncedSearch;
  const showSearchDropdown = !selected && debouncedSearch.length >= 3 && searchIsCurrent;

  return (
    <>
      <LabeledField
        label="XIFIN Ordering Physician"
        labelVariant="sentence"
        editOnHover
        showEditIcon
        onEdit={isError ? undefined : open}
        isLoading={isLoading || updateState.isLoading || clearState.isLoading}
        value={
          isError
            ? 'Unable to load'
            : current
              ? physicianLabel(current)
              : currentNpi || 'Not assigned'
        }
      />
      {isError ? (
        <Alert color="red" title="Unable to load XIFIN Ordering Physician">
          The assigned XIFIN ordering physician could not be loaded. Editing and clearing are
          disabled until it can be loaded.
        </Alert>
      ) : (
        currentNpi &&
        (!current || !current.isRegistered || current.isDeactivated) && (
          <Alert color="yellow" title="Assigned NPI needs attention">
            {!current
              ? 'This NPI is not in the internal NPI directory.'
              : !current.isRegistered
                ? 'This NPI is not in the internal NPI directory.'
                : 'This assigned NPI is deactivated in the internal NPI directory.'}{' '}
            You can replace or clear it by editing this field.
          </Alert>
        )
      )}
      <Modal
        opened={opened && !confirmingClear}
        onClose={closeModal}
        title="XIFIN Ordering Physician"
        size="lg">
        <Stack>
          {current && (
            <Text size="sm" fw={600}>
              Currently: {physicianLabel(current)}
            </Text>
          )}
          <Text size="sm" c="dimmed">
            Manages the patient&apos;s XIFIN ordering physician NPI. XIFIN uses it as the claim
            ordering/referring physician when no usable transmission-report physician exists. This
            value is also used by the Following Practitioner assignment in Tasks.
          </Text>
          {!manual && (
            <Combobox
              store={combobox}
              onOptionSubmit={(value) => {
                const provider = options.find((option) => option.npi === value) ?? null;
                setSelected(provider);
                if (provider) {
                  setSearch(physicianLabel(provider));
                }
                combobox.closeDropdown();
              }}>
              <Combobox.Target>
                <TextInput
                  label="Search registered NPI records"
                  placeholder="Type a physician name or NPI"
                  value={search}
                  rightSection={isFetching ? <Loader size="xs" /> : undefined}
                  onChange={(event) => {
                    const nextSearch = event.currentTarget.value;
                    setSearch(nextSearch);
                    setSelected(null);
                    if (nextSearch.trim().length >= 3) {
                      combobox.openDropdown();
                    } else {
                      combobox.closeDropdown();
                    }
                  }}
                  onFocus={() => {
                    if (debouncedSearch.length >= 3 && !selected) {
                      combobox.openDropdown();
                    }
                  }}
                />
              </Combobox.Target>
              {showSearchDropdown && (
                <Combobox.Dropdown>
                  <Combobox.Options aria-busy={isFetching}>
                    {isFetching && (
                      <Combobox.Empty>Loading registered physicians...</Combobox.Empty>
                    )}
                    {!isFetching &&
                      options.map((provider) => (
                        <Combobox.Option
                          key={provider.npi}
                          value={provider.npi}
                          disabled={provider.isDeactivated}>
                          <Text fw={600}>{physicianName(provider) || `NPI ${provider.npi}`}</Text>
                          <Text size="xs" c="dimmed">
                            {[
                              `NPI ${provider.npi}`,
                              [provider.city, provider.state].filter(Boolean).join(', '),
                              provider.isDeactivated ? 'Deactivated' : ''
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </Text>
                        </Combobox.Option>
                      ))}
                    {!isFetching && options.length === 0 && (
                      <Combobox.Empty>No registered physicians found.</Combobox.Empty>
                    )}
                  </Combobox.Options>
                </Combobox.Dropdown>
              )}
            </Combobox>
          )}
          {selected && !manual && <Text size="sm">Selected: {physicianLabel(selected)}</Text>}
          {manual && (
            <>
              <TextInput
                label="NPI"
                value={npi}
                error={npiError}
                description="Enter all 10 digits. The check digit must be valid."
                onBlur={() => setNpiTouched(true)}
                onChange={(event) => {
                  setNpi(event.currentTarget.value.replace(/\D/g, '').slice(0, 10));
                }}
              />
              <Group grow>
                <TextInput
                  label="First name"
                  value={firstName}
                  onChange={(event) => setFirstName(event.currentTarget.value)}
                />
                <TextInput
                  label="Last name"
                  value={lastName}
                  onChange={(event) => setLastName(event.currentTarget.value)}
                />
              </Group>
              <TextInput
                label="Credential (optional)"
                placeholder="DO"
                value={credential}
                onChange={(event) => setCredential(event.currentTarget.value)}
              />
            </>
          )}
          <Button
            variant="subtle"
            disabled={isError}
            onClick={() => {
              setManual((value) => !value);
              setSelected(null);
              setSearch('');
              setNpi('');
              setNpiTouched(false);
              setFirstName('');
              setLastName('');
              setCredential('');
            }}>
            {manual ? 'Search registered physicians' : 'Add unregistered physician'}
          </Button>
          <Group justify="space-between">
            <Button
              color="red"
              variant="light"
              disabled={isError || (!current && !currentNpi)}
              onClick={() => setConfirmingClear(true)}>
              Clear
            </Button>
            <Group>
              <Button variant="default" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                loading={updateState.isLoading}
                disabled={isError || (manual ? !manualValid : !selected)}
                onClick={() => void save()}>
                Save
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
      <ConfirmationModal
        isOpen={confirmingClear}
        isLoading={clearState.isLoading}
        onClose={() => setConfirmingClear(false)}
        onConfirm={() => void handleClear()}
        title="Clear XIFIN Ordering Physician?"
        description="This physician will no longer be used as the patient-level ordering/referring physician for XIFIN claims and will also be removed as the Following Practitioner in Tasks."
        primaryButtonText="Clear"
      />
    </>
  );
}
